import { useMemo, useState, useRef } from "react";
import { ContextMenuControl, IconControl, Loading } from "spark-core-dx/components";
import { useVisualFormContext } from "../../../../contexts/VisualFormContext";
import { useDashboardDomainContext } from "../../../../DashboardDomainProvider";
import { DeleteVisualDialog } from "../../../dialogs/DeleteVisualDialog";
import { CardDesignModeOptions, CardViewModeOptions } from "./CardContextMenuLib";
import { useVisualExportContext } from "../../../../contexts/VisualExportContext";
import { useFullscreen } from "../../../../contexts/FullScreenContext";
import UpdatedInfoControl from "../../../UpdatedInfoControl";
import {
  VISUAL_EXECUTION_MODE,
  getVisualExecutionModeLabel,
  normalizeVisualExecutionMode,
  resolveVisualRuntimeExecutionMode,
} from "../../../../visualExecutionModes";

const formatRuntimeRefresh = (value) => {
  if (!value) {
    return "";
  }

  const timestamp = value instanceof Date ? value.getTime() : Number(value);
  if (!Number.isFinite(timestamp)) {
    return "";
  }

  const deltaMs = Math.max(0, Date.now() - timestamp);
  const deltaMinutes = Math.floor(deltaMs / 60000);
  if (deltaMinutes <= 0) {
    return "Just now";
  }

  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `${deltaHours}h ago`;
  }

  const deltaDays = Math.floor(deltaHours / 24);
  return `${deltaDays}d ago`;
};

export const CardTileHeader = ({ card, visual, runtime = null, isOverlay = false }) => {
  const {
    dashboardLib,
    isViewMode,
    isFilterDebouncing,
    activeContextMenu,
    handleAssignActiveContextMenu
  } = useDashboardDomainContext();

  const [isLocked,setIsLocked] = useState(!!visual?.IsLocked);
  const contextMenuPortalRef = useRef(null);
  const { openVisualEdit, openVisualClone } = useVisualFormContext();
  const { openExportDialog } = useVisualExportContext();
  const { isFullscreen, openFullscreen, closeFullscreen } = useFullscreen();
 
  // FIX 1: logical && (not bitwise &), so the rule is consistent
  const canOpenContext = (isFullscreen && isOverlay) || (!isFullscreen && !isOverlay);

  const showLoadingIndicator = isFilterDebouncing && !visual?.IsLocked;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Normalize IDs to strings to avoid === failures when types differ
  const visualId = visual?.VisualId != null ? String(visual.VisualId) : "";
  const activeId = activeContextMenu != null ? String(activeContextMenu) : "";
  const executionMode = runtime?.executionMode ?? resolveVisualRuntimeExecutionMode(
    normalizeVisualExecutionMode(
      visual?.ModelKind,
      visual?.ExecutionMode,
      visual?.DefaultQueryState?.SupportsRealTime ?? true
    ),
    isViewMode
  );
  const executionModeLabel = executionMode === VISUAL_EXECUTION_MODE.SERVER
    ? "Published"
    : executionMode === VISUAL_EXECUTION_MODE.SERVER_WORKSPACE
      ? "Design"
      : getVisualExecutionModeLabel(executionMode);
  const refreshLabel = formatRuntimeRefresh(runtime?.lastLoadedAt);
  const contextMenuStyle = useMemo(() => {
    const isFirstColumnCard = Number(card?.x ?? -1) === 0 && Number(card?.w ?? 0) === 1;
    return {
      top: "2.5rem",
      left: isFirstColumnCard ? "0rem" : undefined,
      right: isFirstColumnCard ? undefined : "0.5rem",
      transformOrigin: isFirstColumnCard ? "top left" : "top right",
    };
  }, [card?.w, card?.x]);

  // Ensure the displayed name starts with an uppercase letter
  const titleText = visual?.Name
    ? `${String(visual.Name).charAt(0).toUpperCase()}${String(visual.Name).slice(1)}`
    : visual?.Name;

  // Also stop propagation on pointer down to avoid early close/focus issues
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // --- Click vs Drag detection to ensure reliability inside draggable items ---
  const clickStateRef = useRef({ x: 0, y: 0, t: 0 });
  const CLICK_MOVE_PX = 5;
  const CLICK_TIME_MS = 400;

  const handleContextMouseDown = (e) => {
    e.stopPropagation();
    clickStateRef.current = { x: e.clientX || 0, y: e.clientY || 0, t: Date.now() };
  };

  const handleContextMouseUp = (e) => {
    e.stopPropagation();
    const s = clickStateRef.current;
    const dx = Math.abs((e.clientX || 0) - s.x);
    const dy = Math.abs((e.clientY || 0) - s.y);
    const dt = Date.now() - s.t;
    if (dx <= CLICK_MOVE_PX && dy <= CLICK_MOVE_PX && dt <= CLICK_TIME_MS) {
      handleAssignActiveContextMenu(e, visualId);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleAssignActiveContextMenu(e, visualId);
  };

  return visual && (
    <div className={`card-tile-header`}>
      <div className="card-tile-header-left">
        {visual?.Icon ? (
          visual?.Description ? (
            <UpdatedInfoControl
              trigger={
                <IconControl
                  className={`card-tile-icon`}
                  iconKey={visual.Icon}
                  color={"var(--primary-text)"}
                />
              }
            >
              {visual.Description}
            </UpdatedInfoControl>
          ) : (
            <IconControl
              className={`card-tile-icon`}
              iconKey={visual.Icon}
              color={"var(--primary-text)"}
            />
          )
        ) : null}
        <div className={`card-tile-title`}>{titleText}</div>
        {!visual?.Icon && visual?.Description && (
          <UpdatedInfoControl>{visual.Description}</UpdatedInfoControl>
        )}
      </div>

      <div className="card-tile-header-right">
        {refreshLabel ? (
          <div className="card-tile-runtime-refresh" title={`Last refreshed ${refreshLabel.toLowerCase()}`}>
            {refreshLabel}
          </div>
        ) : null}
        {executionModeLabel ? (
          <span className="card-tile-runtime-badge">{executionModeLabel}</span>
        ) : null}
        {showLoadingIndicator && (
          <div className="card-tile-loading">
            <Loading type="spinner-2" size="1rem" />
          </div>
        )}

        {(visual?.IsLocked || isLocked) ? (
          <IconControl
            className={`card-tile-locked-icon locked`}
            iconKey={"lock"}
            color={"var(--primary-text)"}
            onMouseDown={stop}
          />
        ) : null}

        <div
          className={'card-header-context-container'}
          ref={contextMenuPortalRef}
          onMouseDown={handleContextMouseDown}
          onMouseUp={handleContextMouseUp}
          onContextMenu={handleContextMenu}
          draggable={false}
          onDragStart={e => e.preventDefault()}
        >
          <IconControl
            className={`card-tile-context-button`}
            iconKey={"viewmore"}
            color={"var(--primary-text)"}
            onMouseDown={handleContextMouseDown}
            onMouseUp={handleContextMouseUp}
            draggable={false}
            onDragStart={e => e.preventDefault()}
          />
          <ContextMenuControl
            openContextMenu={canOpenContext ? activeId === visualId : false}
            setOpenContextMenu={() => handleAssignActiveContextMenu(null, null)}
            contextMenuStyle={contextMenuStyle}
            portalLocation={contextMenuPortalRef.current}
          >
            {isViewMode ? (
              <CardViewModeOptions
                visual={visual}
                removeContextMenu={() => handleAssignActiveContextMenu(null, null)}
                onFullScreen={!isFullscreen ? () => openFullscreen(card) : () => closeFullscreen()}
                onExport={() => { openExportDialog(visual); }}
              />
            ) : (
              <CardDesignModeOptions
                visual={visual}
                removeContextMenu={() => handleAssignActiveContextMenu(null, null)}
                onFullScreen={!isFullscreen ? () => openFullscreen(card) : () => closeFullscreen()}
                onEdit={openVisualEdit}
                onClone={(visual) => {
                    const clonedVisualDraft = dashboardLib.buildClonedVisualDraft(visual);
                    if (clonedVisualDraft) {
                        openVisualClone(clonedVisualDraft);
                    }
                }}
                onHide={(visual) => {
                    dashboardLib.hideVisual(visual);
                }}
                onDelete={() => setIsDeleteOpen(true)}
                onLock={(visual)=>{
                    setIsLocked(!visual.IsLocked);
                    dashboardLib.toggleVisualLock(visual);
                }}
              />
            )}
          </ContextMenuControl>
        </div>
      </div>

      <DeleteVisualDialog
        visual={visual}
        isActive={isDeleteOpen}
        closeDialog={() => setIsDeleteOpen(false)}
      />
    </div>
  );
};
